import classNames from 'clsx';
import { ReactComponent as PlayIcon } from 'mrg-icons/src/mailru/actions/play.svg';
import React, { memo, useCallback, useRef, useState } from 'react';
import type { EViewMode } from 'reactApp/modules/settings/settings.types';
import type { Kind } from 'reactApp/types/Tree';
import { sendDatalistThumbDwh } from 'reactApp/ui/Datalist/DataList.helpers';
import type { IThumb } from 'reactApp/ui/DataListItem/DataListItem.helpers';
import { FileIcon } from 'reactApp/ui/FileIcon/FileIcon';
import { getIconType } from 'reactApp/ui/FileIcon/getIconType';
import { ImageCancelable } from 'reactApp/ui/ImageCancelable/ImageCancelable';
import { withToken } from 'reactApp/utils/withToken';

import styles from '../DataListGalleryItem.css';

export const PicComponent = ({
    ext,
    thumbUrl,
    kind,
    view,
}: {
    ext: string;
    thumbUrl: IThumb;
    kind: Kind;
    view: EViewMode.fastscroll | EViewMode.gallery;
}) => {
    const iconType = getIconType({ ext: ext.toLowerCase() });

    const [isLoaded, setLoaded] = useState<boolean>(false);

    const src = withToken(thumbUrl?.src);
    const srcSet = withToken(thumbUrl?.srcSet);

    const startTime = useRef(performance.now());

    const handleThumbLoad = useCallback(() => {
        setLoaded(true);

        const endTime = performance.now();

        sendDatalistThumbDwh('load', view, kind, Math.round(endTime - startTime.current));
    }, [kind, view]);

    const handleThumbError = useCallback(() => {
        const endTime = performance.now();

        sendDatalistThumbDwh('fail', view, kind, Math.round(endTime - startTime.current), src);
    }, [kind, view, src]);

    return (
        <div className={styles.container}>
            {!isLoaded && (
                <div className={styles.icon}>
                    <FileIcon size="l" type={iconType} />
                </div>
            )}
            <div
                className={classNames({
                    [styles.imageContainer]: true,
                    [styles.imageContainer_loaded]: isLoaded,
                })}
            >
                <ImageCancelable
                    className={classNames({
                        [styles.thumb]: true,
                        [styles.thumb_loaded]: isLoaded,
                    })}
                    draggable={false}
                    src={src}
                    srcSet={srcSet ? `${srcSet} 2x` : ''}
                    alt=""
                    onError={handleThumbError}
                    onLoad={handleThumbLoad}
                />
            </div>
            {isLoaded && kind === 'video' && (
                <div className={styles.play}>
                    <PlayIcon height={14} width={14} />
                </div>
            )}
        </div>
    );
};

export const Pic = memo(PicComponent);
